import { makeAutoObservable } from "mobx";
import { RArray } from "../../collections";
import { Availability } from "../Availability";
import { CrossSellingListProvider, } from "../CrossSellingListProvider";
import { OnTimeFulfillmentTime, } from "../FulfillmentTime";
import { NullPackingAlgorithm, PackingAlgorithm, } from "../PackingAlgorithm";
/**
 * A parent container for all information about the restaurant (menu, timezone, etc...)
 */
export class Restaurant {
    constructor(params) {
        this._fulfillmentTimes = null;
        this._fulfillmentMethods = null;
        this._paymentMethods = null;
        this._paymentChannels = null;
        this._franchiseName = null;
        this.id = params.id;
        this.menu = params.menu;
        this.packingAlgorithm = params.packingAlgorithm;
        this.timezone = params.timezone;
        this.appOrigin = params.appOrigin;
        this.orderingPolicy = params.orderingPolicy;
        this.orderingAvailability = params.orderingAvailability;
        this.onlineOrderingSettings = params.onlineOrderingSettings;
        this.orderingHours = params.orderingHours;
        this.holidays = params.holidays;
        this.minOrderValue = params.minOrderValue;
        this.crossSellingListProvider = params.crossSellingListProvider;
        makeAutoObservable(this);
    }
    static create(params) {
        const packagingContainerTypes = params.packagingContainerTypes;
        const packingAlgorithm = packagingContainerTypes.isEmpty
            ? new NullPackingAlgorithm()
            : new PackingAlgorithm({ packagingContainerTypes });
        const orderingAvailability = params.orderingPossible
            ? Availability.available()
            : Availability.unavailable("OrderingUnavailable");
        const crossSellingListProvider = CrossSellingListProvider.create({
            crossSellingListRepository: params.crossSellingListRepository,
            menuProductTypes: params.menu.productTypes,
            allowedAutomaticProductCategories: params.allowedAutomaticCrossSellingProductCategories,
            automaticWeights: params.automaticCrossSellingWeights,
            enableManualCrossSelling: params.onlineOrderingSettings.enableManualCrossSelling,
            enableAutomaticCrossSelling: params.onlineOrderingSettings.enableAutomaticCrossSelling,
        });
        const restaurant = new Restaurant({
            id: params.id,
            menu: params.menu,
            packingAlgorithm,
            timezone: params.timezone,
            appOrigin: params.appOrigin,
            orderingPolicy: params.orderingPolicy,
            orderingAvailability,
            onlineOrderingSettings: params.onlineOrderingSettings,
            orderingHours: params.orderingHours,
            holidays: params.holidays,
            minOrderValue: params.minOrderValue,
            crossSellingListProvider,
        });
        restaurant._fulfillmentTimes = params.checkoutInfo.fulfillmentTimes;
        restaurant._fulfillmentMethods = params.checkoutInfo.fulfillmentMethods;
        restaurant._paymentMethods = params.checkoutInfo.paymentMethods;
        restaurant._franchiseName = params.checkoutInfo.franchiseName;
        restaurant._paymentChannels = params.paymentChannels;
        return restaurant;
    }
    /**
     * Returns possible fulfillment times, or `null` if not loaded yet.
     */
    get fulfillmentTimes() {
        return this._fulfillmentTimes;
    }
    /**
     * Returns possible fulfillment times at current moment and current moment itself, or `null` if not loaded yet.
     */
    currentFulfillmentTimes(now) {
        if (this.fulfillmentTimes === null) {
            return null;
        }
        const times = this.fulfillmentTimes.map((t) => t.type === "ASAP" ? now : t.date);
        return { times, now };
    }
    /**
     * Returns possible fulfillment times at given date.
     * TODO: getting fulfillment times in one way, no matter if its today or not, is a bit weird.
     */
    dateFulfillmentTimes(date) {
        var _a, _b;
        if (date.isToday) {
            return ((_b = (_a = this.fulfillmentTimes) === null || _a === void 0 ? void 0 : _a.mapOptional((item) => item.type === "OnTime" ? item : null)) !== null && _b !== void 0 ? _b : RArray.empty());
        }
        else {
            return this.orderingHours
                .fulfillmentDates(date, this.holidays)
                .map((date) => new OnTimeFulfillmentTime({ date, label: null }));
        }
    }
    get fulfillmentMethods() {
        return this._fulfillmentMethods;
    }
    get isNoContactDeliveryAvailable() {
        var _a, _b;
        return (_b = (_a = this.fulfillmentMethods) === null || _a === void 0 ? void 0 : _a.includes("NoContactDelivery")) !== null && _b !== void 0 ? _b : false;
    }
    get paymentMethods() {
        if (this._paymentMethods !== null && this.appOrigin === "QrWaiter") {
            return this._paymentMethods.filtered((paymentMethod) => paymentMethod !== "Cash" && paymentMethod !== "Card");
        }
        return this._paymentMethods;
    }
    get paymentChannels() {
        return this.paymentMethods !== null &&
            this.paymentMethods.includes("Online")
            ? this._paymentChannels
            : RArray.empty();
    }
    get franchiseName() {
        return this._franchiseName;
    }
}
